import { createSlice } from '@reduxjs/toolkit';
import sum from 'lodash/sum';
import { callRPC } from '../../services/databaseService';
import { dispatch, store } from '../store';

// ----------------------------------------------------------------------

export type TabOrderMenuProduct = {
  id: number;
  name: string;
  description?: string;
  price: number;
  salePrice?: number;
  sale?: boolean;
  photo_url?: string;
  category_id?: number;
  venue_id?: string;
};

export type TabOrderMenuCategory = {
  id: number;
  name: string;
  data: TabOrderMenuProduct[];
};

export type TabOrderCartItem = {
  product_id: number;
  name: string;
  quantity: number;
  price: number;
  salePrice: number;
  sale: boolean;
  comments: string;
};

type TabOrderState = {
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  tabId: number | null;
  venueId: string | null;
  customerName: string | null;
  tableLabel: string | null;
  activeStep: number;
  categories: TabOrderMenuCategory[];
  cart: TabOrderCartItem[];
  search: string;
  createdOrder: {
    order_id: number;
    ordercode: number;
    total: number;
  } | null;
};

const initialState: TabOrderState = {
  isLoading: false,
  isSubmitting: false,
  error: null,
  tabId: null,
  venueId: null,
  customerName: null,
  tableLabel: null,
  activeStep: 0,
  categories: [],
  cart: [],
  search: '',
  createdOrder: null,
};

const slice = createSlice({
  name: 'tabOrder',
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    startSubmitting(state) {
      state.isSubmitting = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.isSubmitting = false;
      state.error = action.payload;
    },
    stopLoading(state) {
      state.isLoading = false;
    },
    initTabOrder(state, action) {
      const { tabId, venueId, customerName, tableLabel } = action.payload;
      state.tabId = tabId;
      state.venueId = venueId;
      state.customerName = customerName || null;
      state.tableLabel = tableLabel || null;
      state.activeStep = 0;
      state.cart = [];
      state.createdOrder = null;
      state.error = null;
      state.search = '';
    },
    getMenuSuccess(state, action) {
      state.isLoading = false;
      state.categories = action.payload;
    },
    setSearch(state, action) {
      state.search = action.payload;
    },
    addToCart(state, action) {
      const item: TabOrderCartItem = action.payload;
      const existing = state.cart.find((c) => c.product_id === item.product_id && c.comments === item.comments);
      if (existing) {
        existing.quantity += item.quantity;
      } else {
        state.cart.push(item);
      }
    },
    setCartItemQuantity(state, action) {
      const { product_id, comments, quantity } = action.payload;
      const existing = state.cart.find((c) => c.product_id === product_id && c.comments === (comments || ''));
      if (!existing) return;
      if (quantity <= 0) {
        state.cart = state.cart.filter((c) => !(c.product_id === product_id && c.comments === (comments || '')));
      } else {
        existing.quantity = quantity;
      }
    },
    removeFromCart(state, action) {
      const { product_id, comments } = action.payload;
      state.cart = state.cart.filter((c) => !(c.product_id === product_id && c.comments === (comments || '')));
    },
    onNextStep(state) {
      state.activeStep += 1;
    },
    onBackStep(state) {
      state.activeStep = Math.max(0, state.activeStep - 1);
    },
    onGotoStep(state, action) {
      state.activeStep = action.payload;
    },
    createSuccess(state, action) {
      state.isSubmitting = false;
      state.createdOrder = action.payload;
      state.activeStep = 2;
      state.cart = [];
    },
    resetTabOrder() {
      return initialState;
    },
  },
});

export default slice.reducer;

export const {
  initTabOrder,
  setSearch,
  addToCart,
  setCartItemQuantity,
  removeFromCart,
  onNextStep,
  onBackStep,
  onGotoStep,
  resetTabOrder,
} = slice.actions;

export function selectTabOrderSubtotal(cart: TabOrderCartItem[]) {
  return sum(
    cart.map((item) => {
      const unit = item.sale ? item.salePrice : item.price;
      return unit * item.quantity;
    })
  );
}

export function selectCartQuantity(cart: TabOrderCartItem[]) {
  return sum(cart.map((item) => item.quantity));
}

// ----------------------------------------------------------------------

function normalizeMenuCategories(raw: any): TabOrderMenuCategory[] {
  if (!raw) return [];

  // menus[] shape (timed menus)
  if (Array.isArray(raw.menus) && raw.menus.length > 0) {
    const categories: TabOrderMenuCategory[] = [];
    raw.menus.forEach((menu: any) => {
      (menu.categories || []).forEach((cat: any) => {
        categories.push({
          id: Number(cat.id),
          name: cat.name,
          data: (cat.data || []).map((p: any) => ({
            id: Number(p.id),
            name: p.name,
            description: p.description,
            price: Number(p.price || 0),
            salePrice: Number(p.salePrice ?? p.saleprice ?? p.price ?? 0),
            sale: Boolean(p.sale),
            photo_url: p.photo_url,
            category_id: Number(p.category_id || cat.id),
            venue_id: p.venue_id,
          })),
        });
      });
    });
    return categories;
  }

  // products[] = categories with nested data
  const products = Array.isArray(raw.products) ? raw.products : Array.isArray(raw) ? raw : [];
  return products.map((cat: any) => ({
    id: Number(cat.id),
    name: cat.name,
    data: (cat.data || []).map((p: any) => ({
      id: Number(p.id),
      name: p.name,
      description: p.description,
      price: Number(p.price || 0),
      salePrice: Number(p.salePrice ?? p.saleprice ?? p.price ?? 0),
      sale: Boolean(p.sale),
      photo_url: p.photo_url,
      category_id: Number(p.category_id || cat.id),
      venue_id: p.venue_id,
    })),
  }));
}

export function loadTabOrderMenu(venueId: string) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const result = await callRPC<any>('get_venue_products', {
        p_venue_id: venueId,
        p_apply_active_menu: true,
      });
      dispatch(slice.actions.getMenuSuccess(normalizeMenuCategories(result)));
    } catch (error: any) {
      dispatch(slice.actions.hasError(error?.message || 'Failed to load menu'));
      throw error;
    }
  };
}

type CreatedTabOrderResult = {
  order_id: number;
  ordercode: number;
  total: number;
};

export function createTabOrderAsVenue() {
  return async () => {
    const { tabOrder } = store.getState() as { tabOrder: TabOrderState };
    const { tabId, cart } = tabOrder;

    if (!tabId) {
      throw new Error('Tab not selected');
    }
    if (!cart.length) {
      throw new Error('Cart is empty');
    }

    dispatch(slice.actions.startSubmitting());
    try {
      const cartItems = cart.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        comments: item.comments || '',
        price: item.price,
        salePrice: item.salePrice,
        sale: item.sale,
      }));

      const result = await callRPC<CreatedTabOrderResult | CreatedTabOrderResult[]>(
        'create_tab_order_as_venue',
        {
          p_tab_id: tabId,
          p_cart_items: cartItems,
          p_service_fee: 0,
          p_service_tip: 0,
          p_discount_id: null,
        }
      );

      const row = Array.isArray(result) ? result[0] : result;
      if (!row?.order_id) {
        throw new Error('Order was not created');
      }

      dispatch(slice.actions.createSuccess({
        order_id: Number(row.order_id),
        ordercode: Number(row.ordercode),
        total: Number(row.total),
      }));

      return row;
    } catch (error: any) {
      const message = error?.message || error?.error_description || 'Failed to create order';
      dispatch(slice.actions.hasError(message));
      throw new Error(message);
    }
  };
}
